import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import Image, { StaticImageData } from 'next/image'
import { useRouter } from 'next/router'
import React, { useState, useEffect } from 'react'
import { useStore } from 'react-redux'
import SvgArrow from '#/assets/images/landing/arrow.svg'
import Button from '#/components/Button'
import Carousel from '#/components/Carousel'
import Link /* , { LinkButton } */ from '#/components/Link'
import Tooltip from '#/components/Tooltip'
import { BREAK_MD, BREAK_SM } from '#/consts'
import { analyticsData } from '#/consts/analytics'
import categories from '#/containers/MapView/components/AnalyticsLayerPanel/categories'
import { logEvent } from '#/services/analytics'
import { initialState } from '#/store/reducers/preferences'
import cn from '#/utils/classnames'
import { setIn } from '#/utils/object'
import { state2MapUrl } from '#/utils/url'
import imgTopHapAdvancedFilters from '@public/images/landing/tophap-advanced-filters.jpg'
import imgTopHapAnalytics from '@public/images/landing/tophap-analytics.jpg'
import imgTopHapArvCalculator from '@public/images/landing/tophap-arv-calculator.jpg'
import imgTopHapAutoCMA from '@public/images/landing/tophap-auto-cma.jpg'
import imgTopHapBrandedPortal from '@public/images/landing/tophap-branded-portal.jpg'
import imgTopHapCMAPlus from '@public/images/landing/tophap-cma-plus.jpg'
import imgTopHapCompare from '@public/images/landing/tophap-compare.jpg'
import imgTopHapExplorerApp from '@public/images/landing/tophap-explorer-app.jpg'
import imgTopHapFeatureAutoCMA from '@public/images/landing/tophap-feature-auto-cma.jpg'
import imgTopHapFeaturesAdvancedFilters from '@public/images/landing/tophap-features-advanced-filters.jpg'
import imgTopHapFeaturesReports from '@public/images/landing/tophap-features-reports.jpg'
import imgTopHapListings from '@public/images/landing/tophap-listings.jpg'
import imgTopHapLotAnalytics from '@public/images/landing/tophap-lot-analytics.jpg'
import imgTopHapMarketAnalytics from '@public/images/landing/tophap-market-analytics.jpg'
import imgTopHapMarket from '@public/images/landing/tophap-market.jpg'
import imgTopHapOffMarketResearch from '@public/images/landing/tophap-off-market-research.jpg'
import imgTopHapPermits from '@public/images/landing/tophap-permits.jpg'
import imgTopHapPropertyDetail from '@public/images/landing/tophap-property-detail.jpg'
import imgTopHapPropertyInsights from '@public/images/landing/tophap-property-insights.jpg'
import imgTopHapPropertySearch from '@public/images/landing/tophap-property-search.jpg'
import imgTopHapReports from '@public/images/landing/tophap-reports.jpg'
import SVGAppStoreButton from '../../../../public/images/landing/app-store-button.svg'
import StudioHero from './StudioHero'
import { avatarSize, companies } from './images'
import sitemapLinks from './sitemap'
import styles from './styles.module.scss'
import testimonials from './testimonial'
import { Props } from './types'

const CookieConsent = dynamic(() => import('#/components/CookieConsent'), { ssr: false })

const imgBaseUrl = '/images/landing'
const mapPage = {
  href: '/map',
  rel: 'nofollow'
}

const Studio: NextPage<Props> = ({
  authenticated /* , currentRole */,
  isMobile,
  isApple
}) => {
  const [allLinks, showAllLinks] = useState(true)
  const [isMounted, setMounted] = useState(false)
  const router = useRouter()
  const store = useStore<TopHap.StoreState>()
  const roleList = [
    'agent',
    'broker',
    'investor',
    'appraiser',
    'buyer',
    'seller',
    'lender'
  ]
  const [displayRole, setDisplayRole] = useState(roleList[0])

  useEffect(() => {
    setMounted(true)
    let selectedRoleIndex = 0
    const intervalId = setInterval(() => {
      const tempSelectedRoleIndex =
        selectedRoleIndex >= roleList.length - 1 ? 0 : selectedRoleIndex + 1
      setDisplayRole(roleList[tempSelectedRoleIndex])
      selectedRoleIndex = tempSelectedRoleIndex
    }, 750)

    return () => clearInterval(intervalId) // This is important
  }, [])

  function toggleAllLinks() {
    showAllLinks(!allLinks)
  }

  function logSectionClick(name: string) {
    logEvent('select_content', {
      content_type: 'landing',
      item_id: `${name}_section`
    })
  }

  function onAnalyticsMetric(metric: TopHap.AnalyticsMetric) {
    const storeState = store.getState()
    let newStore = setIn(storeState, 'preferences.map.descriptive', {
      ...initialState.map.descriptive,
      enabled: true,
      metric
    })
    newStore = setIn(storeState, 'preferences.map.properties.enabled', false)

    if (storeState.ui.viewport.width <= BREAK_SM) {
      newStore = setIn(newStore, 'ui.sider.visible', false)
    }

    router.push(state2MapUrl(newStore))
  }

  type Block = {
    name: string;
    imgAlt: string;
    image: StaticImageData;
    title: React.ReactNode;
    description: string;
    badge?: React.ReactNode;
  }

  function renderInfoSection({
    imgAlt,
    image,
    title,
    description
  }: Omit<Block, 'name'>) {
    return (
      <div className={cn(styles['th-section'], styles['th-info-section'])}>
        <div className={cn('container', styles['th-section-content'])}>
          <div className={styles['th-section-img']}>
            <Image
              alt={imgAlt}
              src={image}
            />
          </div>

          <div className={styles['th-section-texts']}>
            <h3 className={styles['th-section-title']}>{title}</h3>
            <p className={styles['th-section-description']}>{description}</p>
          </div>
        </div>
      </div>
    )
  }

  function renderFeatureBlock({
    name,
    imgAlt,
    image,
    title,
    description,
    badge
  }: Omit<Block, 'imgWidth' | 'imgHeight'>) {
    return (
      <div className={styles['th-feature-block']}>
        <Link {...mapPage} onClick={() => logSectionClick(name)}>
          <div className={styles['th-block-img']}>
            <div className='absolute z-10 p-3'>{badge}</div>
            <Image
              alt={imgAlt}
              src={image}
            />
          </div>
        </Link>
        <div className={styles['th-block-texts']}>
          <h3 className={styles['th-block-title']}>{title}</h3>
          <p className={styles['th-block-description']}>{description}</p>
        </div>
      </div>
    )
  }
  function renderNewDetailPageBlock() {
    return (
      <div className='relative overflow-hidden pb-2 sm:pb-0'>
        <div className='bg-gray-900 pt-10 sm:pt-16 lg:overflow-hidden lg:pb-14 lg:pt-8'>
          <div className='mx-auto max-w-7xl lg:px-8'>
            <div className='lg:grid lg:grid-cols-2 lg:gap-8'>
              <div className='mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 sm:text-center lg:flex lg:items-center lg:px-0 lg:text-left'>
                <div className='lg:py-24'>
                  <span className='inline-flex items-center rounded-full bg-primary px-3 py-0.5 text-sm font-medium text-white'>
                    New
                  </span>
                  <h2 className='mt-4 text-4xl font-extrabold !leading-tight tracking-tight text-white sm:mt-5 sm:text-6xl lg:mt-6 xl:text-6xl'>
                    <span className='block'>Designed for the</span>
                    {' '}
                    <span className='block text-indigo-400'>
                      modern
                      {' '}
                      {displayRole}
                    </span>
                  </h2>
                  <p className='mt-3 text-base text-gray-300 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl'>
                    A completely new property detail page experience designed
                    for anyone researching real estate at scale.
                  </p>
                  <div className='mt-10 sm:mt-12'>
                    <form action='#' className='sm:mx-auto sm:max-w-xl lg:mx-0'>
                      {renderTryButton()}
                    </form>
                  </div>
                </div>
              </div>
              <div className='-mb-16 mt-12 sm:-mb-48 md:mb-0 lg:relative lg:m-0'>
                <div className='mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0'>
                  <Image
                    alt=''
                    height={
                      isMobile?.phone ? 243 : isMobile?.tablet ? 407 : 574
                    }
                    src={`${imgBaseUrl}/tophap-property-detail.png`}
                    width={isMobile?.phone ? 358 : isMobile?.tablet ? 600 : 846}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  function renderTryButton() {
    return (
      <Link className='inline-block' {...mapPage}>
        <Button className={styles['th-explore-button']}>
          {authenticated ? 'Try Now' : 'Get Started'}
          <SvgArrow className={styles['th-arrow-icon']} />
        </Button>
      </Link>
    )
  }
  return (
    <div className={cn(styles['th-landing'], 'th-no-header-space')}>
      <div className={styles['th-header-back']} />
      <StudioHero />
      <Carousel
        autoplay
        autoplaySpeed={7500}
        classes={{
          root: styles['th-info-sliders'],
          carousel: styles['th-carousel'],
          nextButton: styles['th-next-button'],
          prevButton: styles['th-prev-button']
        }}
        isMobile={isMobile?.any}
      >
        {renderInfoSection({
          imgAlt: 'Gain Deeper Market Knowledge',
          image: imgTopHapMarket,
          title: (
            <>
              Gain Deeper Market
              <br />
              <strong>Knowledge</strong>
            </>
          ),
          description:
            'Master​ any market by uncovering microtrends and insights that drive property values.'
        })}
        {renderInfoSection({
          imgAlt: 'Supercharge Your Research',
          image: imgTopHapCompare,
          title: (
            <>
              <strong>Supercharge</strong>
              {' '}
              Your Research
            </>
          ),
          description:
            'Utilize​ investor-grade real estate analysis tools to save time while researching local and unfamiliar properties and regions.'
        })}

        {renderInfoSection({
          imgAlt: 'Expand Your Territory',
          image: imgTopHapListings,
          title: (
            <>
              <strong>Expand</strong>
              {' '}
              Your Territory
            </>
          ),
          description:
            'Grow​ your business outside of your home territory. Easily analyze any property, neighborhood, city, zip, or county nationally.'
        })}
        {renderInfoSection({
          imgAlt: 'Build Trust & Confidence',
          image: imgTopHapPropertyDetail,
          title: (
            <>
              <strong>Build</strong>
              <br />
              Trust & Confidence
            </>
          ),
          description:
            'Harness​ the power of data and AI to build confidence in real estate decisions and advice.'
        })}
        {renderInfoSection({
          imgAlt: 'Your Competitive Advantage',
          image: imgTopHapAnalytics,
          title: (
            <>
              Your Competitive
              <br />
              <strong>Advantage</strong>
            </>
          ),
          description:
            'Access​ the most comprehensive research tools to position yourself as an expert in any market, and deliver the data-driven insights that your clients need.'
        })}
        {renderInfoSection({
          imgAlt: 'TopHap Advanced Filters',
          image: imgTopHapAdvancedFilters,
          title: (
            <>
              Get granular with
              <br />
              <strong>150+ advanced filters</strong>
            </>
          ),
          description:
            'Slice property results along any number of filters. Combine filters or exclude them. Search by range or exact number.'
        })}
        {renderInfoSection({
          imgAlt: 'TopHap PDF reports',
          image: imgTopHapReports,
          title: (
            <>
              Export world-class
              <br />
              <strong>PDF Reports</strong>
            </>
          ),
          description:
            'Create stunning Property and CMA reports 24/7 with the click of a button.'
        })}
        {renderInfoSection({
          imgAlt: 'TopHap Auto CMA',
          image: imgTopHapAutoCMA,
          title: (
            <>
              Find and compare properties with
              <br />
              <strong>Auto CMA</strong>
            </>
          ),
          description:
            'Detailed comparable analysis has never been this easy. Choose any subject property and TopHap instantly adds 6 comparable properties.'
        })}
      </Carousel>

      <div className='mt-6 text-center md:py-3'>{renderTryButton()}</div>

      {isApple && (
        <section className={cn(styles['th-section'], styles['th-explorer-section-test'])}>
          <div className={cn('container', styles['th-section-content'], 'pl-[15px] sm:pl-10')}>
            <div className={cn(styles['th-section-texts'])}>
              <h3 className={cn(styles['th-section-title'])}>Try the new <span className='text-white'>TopHap</span> Explorer iOS App</h3>
              <p className={cn(styles['th-section-description'])}>
                Use our iOS companion app to instantly locate properties
                simply by pointing your phone. Drive through neighborhoods and see
                all Active, Pending and Sold properties.
              </p>
              <a
                className={cn(styles['th-academy-button'], 'justify-start justify-items-start')}
                href='https://apps.apple.com/us/app/tophap-explorer/id1551845829'
                rel='noreferrer'
                target='_blank'
              >
                <SVGAppStoreButton />
              </a>
            </div>
          </div>
          <div className={cn('overflow-hidden relative w-[780px] h-[755px] z-10', styles['th-section-img'])}>
            <Image
              alt='TopHap Academy'
              className='object-cover object-left'
              src={imgTopHapExplorerApp}
            />
          </div>
        </section>
      )}

      <section
        className={cn(styles['th-section'], styles['th-main-features-section'])}
      >
        {/* <div className='th-section-title'>Features</div> */}
        <div className={cn('container', styles['th-section-content'])}>
          <div className='mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3'>
            {renderFeatureBlock({
              name: 'tophap_reports_features',
              imgAlt: 'TopHap reports',
              image: imgTopHapFeaturesReports,
              title: (
                <>
                  Stunning
                  {' '}
                  <strong>PDF Reports</strong>
                </>
              ),
              description:
                'Impress clients with visually-stunning property and CMA reports that you can create 24/7 at the click of a button.',
              badge: (
                <span className='inline-flex items-center rounded-full bg-primary px-3 py-0.5 text-sm font-medium text-white'>
                  New
                </span>
              )
            })}
            {renderFeatureBlock({
              name: 'advanced_filters_features',
              imgAlt: 'Advanced Filters',
              image: imgTopHapFeaturesAdvancedFilters,
              title: (
                <>
                  150+
                  {' '}
                  <strong>Advanced</strong>
                  {' '}
                  Filters
                </>
              ),
              description:
                'Create highly-targeted property lists by combining any of the 150+ advanced filters on TopHap.',
              badge: (
                <span className='inline-flex items-center rounded-full bg-primary px-3 py-0.5 text-sm font-medium text-white'>
                  New
                </span>
              )
            })}
            {renderFeatureBlock({
              name: 'auto_cma_features',
              imgAlt: 'Auto CMA',
              image: imgTopHapFeatureAutoCMA,
              title: (
                <>
                  <strong>Auto</strong>
                  {' '}
                  CMA
                </>
              ),
              description:
                'Quickly and easily get the 6 best comparables for your target property. Then export your findings into a gorgeous report.',
              badge: (
                <span className='inline-flex items-center rounded-full bg-primary px-3 py-0.5 text-sm font-medium text-white'>
                  New
                </span>
              )
            })}
            {renderFeatureBlock({
              name: 'visual_market_analytics',
              imgAlt: 'Visual Market Analytics',
              image: imgTopHapMarketAnalytics,
              title: (
                <>
                  Visual
                  {' '}
                  <strong>Market Analytics</strong>
                </>
              ),
              description:
                'Powerful analytics and map layers enable you to uncover insights about regions, neighborhoods, individual properties and lots in seconds.'
            })}
            {renderFeatureBlock({
              name: 'property_insights',
              imgAlt: 'Property Insights',
              image: imgTopHapPropertyInsights,
              title: (
                <>
                  Property
                  {' '}
                  <strong>Insights</strong>
                </>
              ),
              description:
                'E​xamine attributes and characteristics of any property on or off-market.'
            })}
            {renderFeatureBlock({
              name: 'lot_analytics',
              imgAlt: 'Lot Analytics',
              image: imgTopHapLotAnalytics,
              title: (
                <>
                  <strong>Lot</strong>
                  {' '}
                  Analytics
                </>
              ),
              description:
                'V​isually examine lot usability, topography and elevation without the need to visit the site.'
            })}
            {renderFeatureBlock({
              name: 'national_property_search',
              imgAlt: 'Natitonal Property Search',
              image: imgTopHapPropertySearch,
              title: (
                <>
                  <strong>National</strong>
                  {' '}
                  Property Search
                </>
              ),
              description:
                'Quickly search all for sale, pending, sold, and rental properties across the entire country.'
            })}
            {renderFeatureBlock({
              name: 'off_market_research',
              imgAlt: 'Off-Market Research',
              image: imgTopHapOffMarketResearch,
              title: (
                <>
                  Off-Market
                  {' '}
                  <strong>Research</strong>
                </>
              ),
              description:
                'Identify properties that are not currently for sale with targeted search criteria.'
            })}
            {renderFeatureBlock({
              name: 'tophap_cma+',
              imgAlt: 'TopHap CMA+',
              image: imgTopHapCMAPlus,
              title: (
                <>
                  TopHap
                  {' '}
                  <strong>CMA+</strong>
                </>
              ),
              description:
                'TopHap’s investor grade comparison engine enables you to see diverging microtrends, compare regions, and evaluate historical changes to markets and properties.'
            })}
            {renderFeatureBlock({
              name: 'arv_calculator',
              imgAlt: 'After Repair Value (ARV) Calculator',
              image: imgTopHapArvCalculator,
              title: (
                <>
                  After Repair Value (ARV)
                  {' '}
                  <strong>Calculator</strong>
                </>
              ),
              description:
                'Reveal the full property improvement potential by adjusting the square footage, room count and property condition.'
            })}
            {renderFeatureBlock({
              name: 'permits',
              imgAlt: 'Permits',
              image: imgTopHapPermits,
              title: <strong>Permits</strong>,
              description:
                'Review full property permit history and analyze full residential building permit activity across the state.'
            })}
            {renderFeatureBlock({
              name: 'permits',
              imgAlt: 'Permits',
              image: imgTopHapBrandedPortal,
              title: (
                <>
                  <strong>Branded</strong>
                  {' '}
                  Portal
                </>
              ),
              description:
                'Invite your clients, grow your network and get lead notifications. Guests can access your plan features, at no cost to them.'
            })}
          </div>
        </div>
      </section>
      {/* {(!currentRole || currentRole === 'free') && (
        <div className='th-section th-start-trial-section'>
          <p>
            Start your 14-day <strong>free trial</strong>
          </p>
          <LinkButton className='th-get-started' href={PRICING_PAGE}>
            Get started
          </LinkButton>
        </div>
      )} */}

      <div className='text-center md:py-4'>{renderTryButton()}</div>
      <div className='mt-6 text-center md:py-3'>
        {renderNewDetailPageBlock()}
      </div>
      <section
        className={cn(styles['th-section'], styles['th-analytics-section'])}
      >
        <h3 className={styles['th-section-title']}>
          Our AI Analyzes
          <br />
          billions of data points
        </h3>
        <p className={styles['th-section-description']}>
          to provide the most extensive real-time real estate insights
        </p>
        <div className={cn('container', styles['th-analytics-metrics'])}>
          {categories.map(category => (
            <div key={category.id} className={styles['th-category']}>
              <div
                className={styles['th-category-title']}
                style={{ color: category.color }}
              >
                <category.Icon />
                {' '}
                {category.title}
              </div>
              <div className={styles['th-metrics']}>
                {category.items.map((e: TopHap.AnalyticsMetric) => (
                  <div
                    key={e}
                    className={styles['th-metric']}
                    onClick={() => onAnalyticsMetric(e)}
                  >
                    <Tooltip
                      tooltip={analyticsData[e].tooltip}
                      trigger='hover'
                      wrap={false}
                    >
                      <span>{analyticsData[e].title}</span>
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section
        className={cn(styles['th-section'], styles['th-company-section'])}
      >
        <h3 className={styles['th-section-title']}>
          Trusted by Industy Leaders
        </h3>
        <div className={cn('container', styles['th-section-content'])}>
          <div className={styles['th-company-logos']}>
            {companies.map(company => (
              <div key={company.name} className={styles['th-company']}>
                <Image
                  key={company.name}
                  alt={company.name}
                  height={company.height}
                  src={`${imgBaseUrl}/logos/${company.name}.png`}
                  width={company.width}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {isMounted && (
        <section
          className={cn(styles['th-section'], styles['th-testimonial-section'])}
        >
          <Carousel
            autoplay
            autoplaySpeed={7500}
            classes={{
              container: 'container',
              carousel: styles['th-carousel'],
              nextButton: styles['th-next-button'],
              prevButton: styles['th-prev-button']
            }}
            isMobile={isMobile?.any}
            responsive={[
              {
                breakpoint: BREAK_MD,
                slidesToShow: 1,
                slidesToScroll: 1
              }
            ]}
            slidesToScroll={2}
            slidesToShow={2}
          >
            {testimonials.map((e, index) => (
              <div key={index} className={styles['th-testimonial']}>
                <div className={styles['th-text']}>
                  {e.testimonial}
                  &nbsp;
                  <span className={styles['th-end-mark']}>”</span>
                </div>
                <div className={styles['th-person']}>
                  <div className={styles['th-avatar']}>
                    <Image
                      alt={e.name}
                      height={avatarSize}
                      src={e.avatar}
                      style={{ height: avatarSize }}
                      width={avatarSize}
                    />
                  </div>
                  <div>
                    <div className={styles['th-name']}>{e.name}</div>
                    {e.license && (
                      <div className={styles['th-license']}>{e.license}</div>
                    )}
                    <div className={styles['th-job']}>{e.job}</div>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </section>
      )}

      <section
        className={cn(
          styles['th-section'],
          styles['th-inman-testimonial-section']
        )}
      >
        <div className={cn('container', styles['th-section-content'])}>
          <div className={styles['th-testimonial-text']}>
            <p>
              When a user browses TopHap’s tools, they’ll quickly understand how
              even a single street is ultimately its own micro market, pulling
              the curtain back on everything that makes real estate worth what
              it’s worth. My take is that there is no better way to understand a
              market than by understanding how it relates to the markets around
              it.
            </p>
            <p>
              After all, markets touch, and like the properties of thermal
              conduction, values spread through direct contact, and TopHap puts
              you in the middle of all that market energy.
            </p>
          </div>
          <a
            className={styles['th-author']}
            href='https://www.inman.com/author/craig-rowe/'
            rel='noopener noreferrer'
            target='_blank'
          >
            - Craig Rowe
          </a>
          <a
            className={styles['th-logo']}
            href='https://www.inman.com/2019/11/08/map-based-data-service-tophap-helps-agents-master-their-market/'
            rel='noopener noreferrer'
            target='_blank'
          >
            <Image
              alt='inman'
              height={199}
              src={`${imgBaseUrl}/logos/inman-logo.png`}
              width={912}
            />
          </a>
        </div>
      </section>

      <section
        className={cn(styles['th-section'], styles['th-sitemap-section'])}
      >
        <div className={cn('container', styles['th-section-content'])}>
          <h3 className={styles['th-section-title']}>
            Browse Properties by State
            <Link href='/sitemap/usa'>View full list</Link>
          </h3>
          <div className={styles['th-links']}>
            <ul className={cn({ [styles['th-show-less']]: !allLinks })}>
              {sitemapLinks.map(e => (
                <li key={e.href}>
                  <Link href={e.href}>{e.title}</Link>
                </li>
              ))}
            </ul>
            <div className={styles['th-show-links']} onClick={toggleAllLinks}>
              {allLinks ? 'Show Less' : 'Show More'}
            </div>
          </div>
        </div>
      </section>
      <CookieConsent />
    </div>
  )
}

export default Studio
